import { defineStore } from "pinia";
import { ref } from "vue";
import { favoritesDB } from "./favorites.db";
import type { FavoriteReport } from "./favorites.types";

// Strip Vue reactivity (the inputs are Pinia store values and reactive refs).
// IndexedDB's structured-clone refuses Proxy-wrapped objects with a
// DataCloneError, and our snapshot is plain JSON-shaped data, so a JSON
// round-trip gives us a safe, fully detached copy.
function toPlain<T>(value: T): T {
  return value === null || value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T);
}

export const useFavoritesStore = defineStore("favorites", () => {
  // Newest-first list of favorites, mirrored from IndexedDB for reactive UI.
  const items = ref<FavoriteReport[]>([]);
  // Quick membership lookup, kept in sync with `items`.
  const ids = ref<Set<number>>(new Set());
  const loaded = ref(false);

  function syncIds() {
    ids.value = new Set(items.value.map((item) => item.id));
  }

  // Hydrate from IndexedDB once; safe to call repeatedly.
  async function load() {
    if (loaded.value) return;
    items.value = await favoritesDB.reports.orderBy("addedAt").reverse().toArray();
    syncIds();
    loaded.value = true;
  }

  function isFavorite(id: number | null | undefined): boolean {
    return typeof id === "number" && ids.value.has(id);
  }

  function get(id: number): FavoriteReport | undefined {
    return items.value.find((item) => item.id === id);
  }

  // Add to favorites (or refresh an existing entry's snapshot). A new entry goes
  // to the front; an existing one keeps its position and `addedAt`.
  async function add(report: Omit<FavoriteReport, "addedAt">) {
    const existing = get(report.id);
    const entry: FavoriteReport = toPlain({
      id: report.id,
      name: report.name,
      savedFilter: report.savedFilter,
      reportData: report.reportData,
      previousReportData: report.previousReportData,
      addedAt: existing?.addedAt ?? Date.now(),
    });
    await favoritesDB.reports.put(entry);
    items.value = existing
      ? items.value.map((item) => (item.id === entry.id ? entry : item))
      : [entry, ...items.value];
    syncIds();
  }

  // Update the stored snapshot of an already-favorited report, leaving its
  // position/`addedAt` untouched. No-op when the report isn't favorited.
  async function syncData(
    id: number,
    data: Pick<FavoriteReport, "name" | "savedFilter" | "reportData" | "previousReportData">,
  ) {
    const existing = get(id);
    if (!existing) return;
    const entry: FavoriteReport = toPlain({ ...existing, ...data });
    await favoritesDB.reports.put(entry);
    items.value = items.value.map((item) => (item.id === id ? entry : item));
  }

  async function remove(id: number) {
    await favoritesDB.reports.delete(id);
    items.value = items.value.filter((item) => item.id !== id);
    syncIds();
  }

  return { items, ids, loaded, load, isFavorite, get, add, syncData, remove };
});
